/**
 * 右滑返回手势的纯状态机（零 DOM 依赖，可单测）。DOM 接线在 useSwipeBack.ts。
 *
 * 语义（5c 定稿，下游别改口径）：
 * - 只认左边缘起手（startX ≤ EDGE_START_MAX），对齐 iOS 原生边缘返回，
 *   避免与消息列表内的横向手势（未来的滑动操作等）抢事件。
 * - 识别期（pending）先走满 10px 的方向赢：水平 → tracking 跟手；
 *   垂直 → 放弃本次手势（滚动优先）。放弃后本根手指不再参与识别。
 * - 松手判定：位移 ≥ 宽度 1/3，或轻扫（平滑速度 ≥ 0.35px/ms 且位移 ≥ 40px）→ commit。
 *   手指停顿超过 100ms 再松手不算轻扫（只看距离）。
 * - 普通浏览器里系统返回手势先接管时会派发 pointercancel → cancel 分支回弹，
 *   互不打架；standalone 没有系统手势，这里是唯一的手势返回路径。
 */

/** 左边缘起手区宽度（px）。 */
export const EDGE_START_MAX = 32;
/** 方向识别阈值（px）：pending 期哪个轴先走满它哪个轴赢。 */
const RECOGNIZE_DX = 10;
/** 松手位移 ≥ 容器宽度的这个比例即 commit。 */
const COMMIT_RATIO = 1 / 3;
/** 轻扫判定：平滑速度（px/ms）与最小位移（px）。 */
const FLICK_VELOCITY = 0.35;
const FLICK_MIN_DX = 40;
/** 松手前停顿超过该时长（ms），轻扫速度作废。 */
const FLICK_STALE_MS = 100;

export interface SwipePoint {
  readonly pointerId: number;
  readonly x: number;
  readonly y: number;
  readonly timeStamp: number;
}

export interface SwipeBackCallbacks {
  /** 每次起手时询问（栈底/手势被禁用时返回 false，down 直接忽略）。 */
  canStart(): boolean;
  /** tracking 期间的跟手位移，已夹在 [0, width]。 */
  setOffset(px: number): void;
  /** 手势结束（仅进过 tracking 才触发）：committed=true 调用方执行返回，false 回弹。 */
  end(committed: boolean): void;
}

export interface SwipeBackGesture {
  /** width = 容器宽度（commit 阈值基准）。 */
  down(p: SwipePoint, width: number): void;
  /** 返回 true 表示手势正处于 tracking（调用方据此 setPointerCapture）。 */
  move(p: SwipePoint): boolean;
  up(p: SwipePoint): void;
  cancel(pointerId: number): void;
}

export function createSwipeBackGesture(callbacks: SwipeBackCallbacks): SwipeBackGesture {
  let phase: 'idle' | 'pending' | 'tracking' = 'idle';
  let id = -1;
  let startX = 0;
  let startY = 0;
  let width = 0;
  let offset = 0;
  let lastX = 0;
  let lastT = 0;
  let velocity = 0;

  function reset(): void {
    phase = 'idle';
    id = -1;
  }

  return {
    down(p, w) {
      // 已有手指在识别/跟手中：后落的手指不参与（多指时第一根赢）
      if (phase !== 'idle') return;
      if (p.x > EDGE_START_MAX || !callbacks.canStart()) return;
      phase = 'pending';
      id = p.pointerId;
      startX = p.x;
      startY = p.y;
      width = Math.max(w, 1);
      offset = 0;
      lastX = p.x;
      lastT = p.timeStamp;
      velocity = 0;
    },

    move(p) {
      if (phase === 'idle' || p.pointerId !== id) return false;
      const dx = p.x - startX;
      const dy = Math.abs(p.y - startY);
      if (phase === 'pending') {
        if (dy >= RECOGNIZE_DX && dy > dx) {
          reset(); // 垂直先走满：让位给滚动
          return false;
        }
        if (dx >= RECOGNIZE_DX && dx > dy) {
          phase = 'tracking';
        } else {
          return false;
        }
      }
      offset = Math.min(Math.max(dx, 0), width);
      callbacks.setOffset(offset);
      const dt = p.timeStamp - lastT;
      if (dt > 0) {
        const inst = (p.x - lastX) / dt;
        velocity = velocity === 0 ? inst : inst * 0.6 + velocity * 0.4;
      }
      lastX = p.x;
      lastT = p.timeStamp;
      return true;
    },

    up(p) {
      if (phase === 'idle' || p.pointerId !== id) return;
      const wasTracking = phase === 'tracking';
      const stale = p.timeStamp - lastT > FLICK_STALE_MS;
      const committed =
        wasTracking &&
        (offset >= width * COMMIT_RATIO ||
          (!stale && velocity >= FLICK_VELOCITY && offset >= FLICK_MIN_DX));
      reset();
      if (wasTracking) callbacks.end(committed);
    },

    cancel(pointerId) {
      if (phase === 'idle' || pointerId !== id) return;
      const wasTracking = phase === 'tracking';
      reset();
      if (wasTracking) callbacks.end(false);
    },
  };
}
